import React, { useEffect, useLayoutEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { ScheduleActionables, ScheduleEmpDetails, ScheduleHeader } from "../../../components/mobile/scheduleDetails";
import { Loader2 } from "lucide-react";
import { setGlobalBackHandler } from "../../../hooks";
import { useNavigate } from "react-router";

const ScheduleScreen: React.FC = () => {
    const { isScheduleDetailLoading, scheduleDetail: schedule } = useSelector((state: RootState) => state.schedule);
    const navigate = useNavigate();

    useLayoutEffect(() => {
        setGlobalBackHandler(() => {
            navigate(-1);
        });
    }, []);

    // If schedule is missing (e.g., on browser reload), navigate back safely
    useEffect(() => {
        if (!isScheduleDetailLoading && !schedule) {
            navigate(-1);
        }
    }, [isScheduleDetailLoading, schedule, navigate]);

    // Fullscreen loader while loading initial data or navigating back if missing
    if (isScheduleDetailLoading && !schedule) {
        return (
            <div className="flex justify-center items-center h-full">
                <Loader2 className="w-8 h-8 text-gray-500 animate-spin" />
            </div>
        );
    }
    if (!schedule) {
        return (
            <div className="flex justify-center items-center h-full">
                <Loader2 className="w-8 h-8 text-gray-500 animate-spin" />
            </div>
        );
    }
    
    return (
        <div className="h-full flex flex-col overflow-hidden md:w-1/2 mx-auto">
            {/* Fixed Header */}
            <div className="z-20 bg-white shadow-md">
                <ScheduleHeader schedule={schedule!} />
            </div>

            {/* Show a small loader when updating details while data already present */}
            {isScheduleDetailLoading && (
                <div className="flex justify-center items-center py-2">
                    <Loader2 className="w-5 h-5 text-gray-500 animate-spin" />
                </div>
            )}

            {/* Scrollable Content Section */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Employee Details */}
                {schedule?.emp_details && <ScheduleEmpDetails schedule={schedule} />}

                {/* Shift Details */}
                {(schedule?.shift?.shift_name || schedule?.shift?.shift_code) && (
                    <div className="border rounded-lg p-4 bg-gray-50 shadow">
                        {schedule?.shift?.shift_name && (
                            <div className="text-sm text-gray-700">{schedule.shift.shift_name}</div>
                        )}
                        {schedule?.shift?.shift_code && (
                            <div className="text-xs text-gray-400">{schedule.shift.shift_code}</div>
                        )}
                    </div>
                )}

                {/* Location */}
                {schedule?.site?.site_name && (
                    <div className="border rounded-lg p-4 bg-gray-50 shadow">
                        <h3 className="text-sm font-semibold text-gray-700">Location</h3>
                        <p className="text-sm text-gray-500">{schedule?.site?.site_name}</p>
                    </div>
                )}

                {/* Department */}
                {schedule?.department?.department_code && schedule?.department?.department_name && (
                    <div className="border rounded-lg p-4 bg-gray-50 shadow">
                        <h3 className="text-sm font-semibold text-gray-700">Department</h3>
                        <p className="text-sm text-gray-500">{schedule?.department?.department_name}</p>
                        <p className="text-sm text-gray-500">
                            Department Code: {schedule?.department?.department_code}
                        </p>
                    </div>
                )}

                {/* Logs */}
                {/* <div className="border rounded-lg p-4 bg-gray-50 shadow">
                    <h3 className="text-sm font-semibold text-gray-700">Logs</h3>
                    <p className="text-sm text-gray-500">Log ID: 123456</p>
                    <p className="text-sm text-gray-500">Last Updated: {new Date().toLocaleDateString()}</p>
                </div> */}

                {/* jobcode */}
                {schedule?.workcode?.workcode_name && (
                    <div className="border rounded-lg p-4 bg-gray-50 shadow">
                        <h3 className="text-sm font-semibold text-gray-700">Code</h3>
                        <p className="text-sm text-gray-500">{schedule?.workcode?.workcode_name}</p>
                    </div>
                )}

                {/* Notes */}
                {schedule?.notes && (
                    <div className="border rounded-lg p-4 bg-gray-50 shadow">
                        <h3 className="text-sm font-semibold text-gray-700">Notes</h3>
                        <p className="text-sm text-gray-500">{schedule?.notes}</p>
                    </div>
                )}

                {/* Custom Fields */}
                {schedule?.custom_fields &&
                    Array.isArray(schedule.custom_fields) &&
                    schedule.custom_fields.length > 0 && (
                        <div className="border rounded-lg p-4 bg-gray-50 shadow">
                            <h3 className="text-sm font-semibold text-gray-700 mb-2">Custom Fields</h3>
                            <ul className="space-y-1">
                                {schedule.custom_fields.map((field, index) => {
                                    const key = Object.keys(field)[0];
                                    const value = field[key];
                                    const valueString = Array.isArray(value) ? value.join(", ") : value;

                                    return (
                                        <li key={index} className="text-sm text-gray-500">
                                            <span className="font-medium text-gray-700">{key}</span> - {valueString}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
            </div>

            {/* Fixed Actionable Buttons */}
            {Object.values(schedule?.action_button || {}).some(Boolean) && <ScheduleActionables schedule={schedule!} />}
        </div>
    );
};

export default ScheduleScreen;
